/**
 * Bright Green Color Preset
 * Яркая зеленая цветовая схема с природными оттенками
 */

import { ColorPreset } from '../chartColorPresets';
import { FONTS } from '../fonts';

export const BRIGHT_GREEN_PRESET: ColorPreset = {
  id: 'bright-green',
  name: 'Bright Green Theme',
  description: 'Яркая зеленая цветовая схема',
  
  elements: {
    lagna: '#dc2626',     // Сохраняем красный для лагны (контраст)
    hora: '#059669',      // Изумрудно-зеленый для хоры
    chandra: '#16a34a',   // Зеленый для Чандры
    planetDefault: '#166534'  // Темно-зеленый для планет
  },
  
  degreeLines: {
    mainStroke: '#10b981',    // Яркий зеленый
    divisionStroke: 'rgba(34, 197, 94, 0.3)',
    majorDivision: 'rgba(5, 150, 105, 0.6)',
    mainWidth: 1.0,
    divisionWidth: 0.8,
    majorDivisionWidth: 1.0
  },

  muhurtaDivisions: {
    // Display mode
    renderMode: 'sectors',            // Use colored sectors instead of division lines

    // Division lines styling (when renderMode = 'divisions')
    centralLineStroke: '#f59e0b',     // Amber for central line (contrast with green)
    centralLineWidth: 2,
    majorDivisionStroke: '#166534',   // Dark green for major divisions (every 12°)
    minorDivisionStroke: '#16a34a',   // Medium green for minor divisions (every 6°)
    majorDivisionWidth: 2,
    minorDivisionWidth: 1,
    majorDivisionLength: 200,         // Wide sector band (matches chakraDimensionsConfig)
    minorDivisionLength: 200,         // Wide sector band (matches chakraDimensionsConfig)

    // Sector styling (when renderMode = 'sectors') - 30 colors for muhurta chakra
    sectorFillColors: [
      '#f0fdf4',  // Sector 1 - Ultra light green
      '#ecfdf5',  // Sector 2 - Very light mint
      '#e6fffa',  // Sector 3 - Light cyan-green
      '#dcfce7',  // Sector 4 - Light green
      '#d1fae5',  // Sector 5 - Light emerald
      '#c6f6d5',  // Sector 6 - Soft green
      '#bbf7d0',  // Sector 7 - Light mint
      '#a7f3d0',  // Sector 8 - Mint green
      '#9ae6b4',  // Sector 9 - Light green
      '#86efac',  // Sector 10 - Fresh green
      '#6ee7b7',  // Sector 11 - Medium mint
      '#5eead4',  // Sector 12 - Teal mint
      '#4fd1c7',  // Sector 13 - Aqua green
      '#34d399',  // Sector 14 - Bright emerald
      '#2dd4bf',  // Sector 15 - Teal emerald
      '#1db584',  // Sector 16 - Dark mint
      '#10b981',  // Sector 17 - Emerald
      '#059669',  // Sector 18 - Deep emerald
      '#047857',  // Sector 19 - Forest green
      '#065f46',  // Sector 20 - Dark forest
      '#14532d',  // Sector 21 - Very dark green
      '#16a34a',  // Sector 22 - Medium green
      '#15803d',  // Sector 23 - Green
      '#166534',  // Sector 24 - Dark green
      '#14532d',  // Sector 25 - Deep green
      '#22c55e',  // Sector 26 - Lime green
      '#16a34a',  // Sector 27 - Forest green
      '#15803d',  // Sector 28 - Dark lime
      '#166534',  // Sector 29 - Hunter green
      '#14532d'   // Sector 30 - Deepest green
    ],
    sectorStroke: '#166534',          // Dark green borders
    sectorStrokeWidth: 0.8,           // Slightly thicker borders
    sectorFillOpacity: 0.7,           // Sector fill opacity
    sectorStrokeOpacity: 0.9,         // Sector stroke opacity (more visible)
    minorDivisionOpacity: 0.8,        // Minor lines opacity

    // Global 360° muhurta chakra intervals
    minorInterval: 6,                 // Minor divisions every 6° (60 total)
    majorInterval: 12,                // Major divisions every 12° (30 total)
    totalSectors: 30,                 // 30 muhurta sectors (each 12°)

    // Sector text styling
    sectorNumberStyle: {
      fontSize: 12,                   // Font size for sector numbers (1-30)
      fontFamily: FONTS.chart.primary, // Font family
      fill: '#15803d',               // Dark green for good contrast
      stroke: '#ffffff',              // White stroke for readability
      strokeWidth: 0.5,               // Thin stroke
      fontWeight: 'bold'              // Bold text
    },

    rulerCodeStyle: {
      fontSize: 10,                   // Font size for ruler codes (Sy, Ch, Ma, etc.)
      fontFamily: FONTS.chart.primary, // Font family
      fill: '#166534',               // Darker green for contrast
      stroke: '#ffffff',              // White stroke for readability
      strokeWidth: 0.3,               // Thinner stroke
      fontWeight: 'normal'            // Normal weight
    }
  },


  muhurtaLabels: {
    // Muhurta position label styling (e.g., "Ml" for chakra longitude)
    fontSize: 14,                     // Font size for position labels
    fontFamily: FONTS.chart.primary,  // Font family
    fill: '#ffffff',                  // White text for contrast on green background
    stroke: '#000000',                // Black stroke for text readability
    strokeWidth: 0.5,                 // Thin stroke for text
    fontWeight: 'bold',               // Bold for visibility
    // Line from center to label
    lineStroke: '#16a34a',            // Green line to match theme
    lineWidth: 2,                     // Line width
    // Background square
    backgroundFill: '#16a34a',        // Green background square
    backgroundStroke: '#15803d',      // Darker green border
    backgroundStrokeWidth: 1,         // Border width
    backgroundSize: 20,               // Size of the square background (20x20px)
    labelOffset: 20                   // Distance offset for label positioning
  },

  labelStyles: {
    default: {
      text: {
        fontSize: 12,
        fontFamily: FONTS.chart.primary,
        fontWeight: 'bold',
        fill: '#14532d',
        stroke: '#ffffff',
        strokeWidth: 0.3
      },
      line: {
        stroke: '#16a34a',
        strokeWidth: 1,
        opacity: 0.8
      },
      background: {
        shape: 'square' as const,
        size: 20,
        fill: '#d1fae5',
        stroke: '#16a34a',
        strokeWidth: 1,
        opacity: 0.9
      },
      labelOffset: 90
    },
    planet: {
      text: {
        fontSize: 12,
        fontFamily: FONTS.chart.primary,
        fontWeight: 'bold',
        fill: '#059669',
        stroke: '#ffffff',
        strokeWidth: 0.5
      },
      line: {
        stroke: '#059669',
        strokeWidth: 1.5,
        opacity: 0.9
      },
      background: {
        shape: 'circle' as const,
        size: 22,
        fill: '#ecfdf5',
        stroke: '#059669',
        strokeWidth: 1.5,
        opacity: 0.95
      },
      labelOffset: 95
    },
    square: {
      text: {
        fontSize: 14,
        fontFamily: FONTS.chart.primary,
        fontWeight: 'bold',
        fill: '#ffffff',
        stroke: 'none',
        strokeWidth: 0
      },
      line: {
        stroke: '#60a5fa',
        strokeWidth: 2,
        opacity: 1
      },
      background: {
        shape: 'square' as const,
        size: 20,
        fill: '#60a5fa',
        stroke: '#3b82f6',
        strokeWidth: 1,
        opacity: 1
      },
      labelOffset: 20
    },
    squareOrange: {
      text: {
        fontSize: 14,
        fontFamily: FONTS.chart.primary,
        fontWeight: 'bold',
        fill: '#ffffff',
        stroke: 'none',
        strokeWidth: 0
      },
      line: {
        stroke: '#f97316',
        strokeWidth: 2,
        opacity: 1
      },
      background: {
        shape: 'square' as const,
        size: 20,
        fill: '#f97316',
        stroke: '#ea580c',
        strokeWidth: 1,
        opacity: 1
      },
      labelOffset: 20
    }
  },

  detailsPanel: {
    background: '#f0fdf4',        // Очень светло-зеленый фон
    sectionBackground: '#ffffff',
    borderDefault: '#bbf7d0',     // Светло-зеленая граница
    borderSection: '#bbf7d0',
    borderRow: '#dcfce7',         // Очень светлая зеленая строка
    planetBorderDefault: '#a7f3d0',
    planetBorderHover: '#10b981',
    planetBorderActive: '#059669',
    planetFocusOutline: '#059669',
    textPrimary: '#14532d',       // Темно-зеленый текст
    textSecondary: '#166534',
    textAccent: '#059669',
    loadingSpinner: '#10b981',
    loadingBackground: '#d1fae5'
  },
  
  interactive: {
    hoverBoxShadow: '0 1px 3px rgba(16, 185, 129, 0.2)',
    focusOutlineOffset: '-1px',
    focusVisibleOutlineWidth: '2px',
    planetBorderDefault: '#a7f3d0',
    planetBorderHover: '#10b981',
    planetBorderActive: '#059669',
    planetFocusOutline: '#059669',
    headerHoverColor: '#059669',
    headerActiveColor: '#047857',
    headerHoverBackground: 'rgba(16, 185, 129, 0.1)',
    sectionHeaderHoverBackground: '#d1fae5'
  },

  chartElements: {
    planetColor: '#059669',
    yogaStroke: '#14532d',
    yogaTextFill: '#14532d',
    textFill: '#166534',
    yogaColors: ['#ecfdf5', '#d1fae5', '#a7f3d0', '#6ee7b7', '#34d399']  // Градиент зеленых
  },

  houseNames: {
    fontSize: 11,
    fontFamily: FONTS.chart.primary,
    fontWeight: 'normal',
    fill: '#166534',
    opacity: 0.7,
    symbolsMode: {
      fontSize: 20,
      fontFamily: FONTS.chart.symbols,
      fontWeight: 'normal',
      dy: 2
    }
  },

  planetSprites: {
    radius: 17,
    strokeWidth: 1,
    fillColor: '#ffffff',
    connectionLineLength: 22,
    dragHighlightColor: '#FFD700',
    dragOtherPlanetsColor: '#9B59B6',
    modes: {
      symbols: { fontSize: 24, fontWeight: 400, fontFamily: FONTS.chart.symbols, dy: 2 },
      short: { fontSize: 16, fontWeight: 600 },
      full: { fontSize: 10, fontWeight: 500 }
    }
  }
};